import React, { useEffect, useState } from "react";
import { Switch, message, Tooltip } from "antd";
import Card from "src/componets/Card";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { BundleInterface } from "src/store/callrecorder/bundlesSlice";
import {
  selectAllFeatureFlags,
  FeatureFlagInterface
} from "src/store/callrecorder/featureFlagsSlice";
import {
  fetchFeatureFlags,
  updateFeatureFlag
} from "src/actions/callrecorder/featureFlagsActions";

const _FeatureFlags = ({ bundle }: { bundle: BundleInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const flags    = useAppSelector(selectAllFeatureFlags);
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (bundle) {
      dispatch(fetchFeatureFlags(bundle.id));
    }
  }, [bundle, dispatch]);

  if (!bundle) {
    return null;
  }

  async function onToggle(flag: FeatureFlagInterface, enabled: boolean) {
    if (!bundle) return;

    setPendingIds(prev => {
      const next = new Set(prev);
      next.add(flag.id);
      return next;
    });

    const result = await dispatch(updateFeatureFlag({
      bundleId: bundle.id,
      id: flag.id,
      enabled
    }));

    setPendingIds(prev => {
      const next = new Set(prev);
      next.delete(flag.id);
      return next;
    });

    if (updateFeatureFlag.fulfilled.match(result)) {
      message.success(`"${flag.name}" ${enabled ? 'enabled' : 'disabled'}`);
    }

    if (updateFeatureFlag.rejected.match(result)) {
      const error = Array.isArray(result.payload) ? result.payload[0] : result.error.message;
      message.error(error || 'Failed to update feature flag');
    }
  }

  return (
    <Card className='v-mb-20'>
      <h4 className='v-content-title'>Feature Flags</h4>

      {flags.length === 0 && (
        <p className='v-text-muted'>No feature flags defined.</p>
      )}

      <div style={{ maxWidth: 640 }}>
        {flags.map(flag => (
          <div
            key={flag.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <div style={{ flex: 1, marginRight: 16 }}>
              <div style={{ fontWeight: 500 }}>
                {flag.name}
                {' '}
                <Tooltip title={`Global default: ${flag.default_enabled ? 'enabled' : 'disabled'}`}>
                  <span style={{ color: '#999', fontSize: 12, fontWeight: 'normal' }}>
                    (default: {flag.default_enabled ? 'on' : 'off'})
                  </span>
                </Tooltip>
              </div>
              {flag.description && (
                <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                  {flag.description}
                </div>
              )}
            </div>

            <Switch
              checked={flag.enabled}
              loading={pendingIds.has(flag.id)}
              onChange={(checked) => onToggle(flag, checked)}
              checkedChildren='on'
              unCheckedChildren='off'
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

export default _FeatureFlags;
